import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { useNotification } from '../../../components/ui/NotificationProvider';
import { supabase } from '../../../lib/supabase';

const LoginForm = () => {
  const { showInfo, showError, showSuccess } = useNotification();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState('idle'); // idle | sending | success
  const [resetError, setResetError] = useState('');
  const navigate = useNavigate();
  const { signIn } = useAuth();

  useEffect(() => {
    try {
      const stored = localStorage?.getItem('mlbp:rememberedCredentials');
      if (stored) {
        const parsed = JSON.parse(stored);
        let decodedPassword = '';
        try {
          decodedPassword = parsed?.password ? atob(parsed?.password) : '';
        } catch (decodeError) {
          console.warn('No se pudo decodificar la contraseña recordada:', decodeError?.message);
        }
        setFormData(prev => ({
          ...prev,
          email: parsed?.email || '',
          password: decodedPassword,
          rememberMe: Boolean(parsed?.email)
        }));
      }
    } catch (storageError) {
      console.warn('No se pudo leer las credenciales recordadas:', storageError?.message);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.email) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Ingrese un correo electrónico válido';
    }
    
    if (!formData?.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const result = await signIn(formData?.email, formData?.password);
      if (result?.data?.user) {
        try {
          if (formData?.rememberMe) {
            let encodedPassword = '';
            try {
              encodedPassword = typeof window !== 'undefined'
                ? window?.btoa(formData?.password || '')
                : btoa(formData?.password || '');
            } catch (encodeError) {
              console.warn('No se pudo codificar la contraseña para recordarla:', encodeError?.message);
            }
            localStorage?.setItem('mlbp:rememberedCredentials', JSON.stringify({
              email: formData?.email,
              password: encodedPassword
            }));
          } else {
            localStorage?.removeItem('mlbp:rememberedCredentials');
          }
        } catch (storageError) {
          console.warn('No se pudo guardar las credenciales recordadas:', storageError?.message);
        }
        navigate('/dashboard');
      } else {
        setErrors({
          general: result?.error?.message || result?.error || 'Credenciales inválidas. Por favor, verifique su información e intente nuevamente.'
        });
      }
    } catch (error) {
      setErrors({
        general: 'Error de conexión. Intente nuevamente.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setResetEmail(formData?.email || '');
    setResetError('');
    setResetStatus('idle');
    setIsResetModalOpen(true);
  };

  const handleCloseResetModal = () => {
    setIsResetModalOpen(false);
    setResetStatus('idle');
    setResetError('');
  };

  const handleResetPasswordRequest = async (event) => {
    event?.preventDefault();
    setResetError('');

    if (!resetEmail) {
      setResetError('Ingresa el correo asociado a tu cuenta.');
      showError?.('Ingresa el correo asociado a tu cuenta.', { title: 'Correo requerido' });
      return;
    }

    try {
      setResetStatus('sending');

      const redirectUrl = typeof window !== 'undefined'
        ? `${window?.location?.origin}/reset-password`
        : undefined;

      const { error } = await supabase?.auth?.resetPasswordForEmail(resetEmail, {
        redirectTo: redirectUrl
      });

      if (error) {
        setResetStatus('idle');
        setResetError(error?.message || 'No se pudo enviar el correo de recuperación.');
        showError?.(error?.message || 'No se pudo enviar el correo de recuperación.');
        return;
      }

      setResetStatus('success');
      showSuccess?.('Te enviamos un enlace para restablecer tu contraseña. Revisa tu bandeja de entrada.', {
        title: 'Correo enviado'
      });
    } catch (error) {
      setResetStatus('idle');
      setResetError('No pudimos iniciar la recuperación. Verifica tu conexión e intenta nuevamente.');
      showError?.('No pudimos iniciar la recuperación. Verifica tu conexión e intenta nuevamente.');
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Email Input */}
        <Input
          label="Correo Electrónico"
          type="email"
          name="email"
          placeholder="ejemplo@empresa.com"
          value={formData?.email}
          onChange={handleInputChange}
          error={errors?.email}
          required
          disabled={isLoading}
        />

        {/* Password Input */}
        <Input
          label="Contraseña"
          type="password"
          name="password"
          placeholder="Ingrese su contraseña"
          value={formData?.password}
          onChange={handleInputChange}
          error={errors?.password}
          required
          disabled={isLoading}
        />

        {/* Remember Me Checkbox */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Checkbox
            label="Recordarme"
            name="rememberMe"
            checked={formData?.rememberMe}
            onChange={handleInputChange}
            disabled={isLoading}
          />

          <button
            type="button"
            onClick={handleForgotPassword}
            className="text-sm text-primary hover:text-primary/80 transition-smooth"
            disabled={isLoading}
          >
            ¿Olvidaste tu contraseña?
          </button>
        </div>

        {/* General Error Message */}
        {errors?.general && (
          <div className="p-3 bg-error/10 dark:bg-error/20 border border-error/20 dark:border-error/40 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <span className="text-sm text-error">{errors?.general}</span>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isLoading}
          iconName="LogIn"
          iconPosition="left"
          disabled={isLoading}
        >
          {isLoading ? 'Iniciando Sesión...' : 'Iniciar Sesión'}
        </Button>

      </form>

      {isResetModalOpen && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/30 dark:bg-black/70 backdrop-blur-sm"
            onClick={handleCloseResetModal}
            aria-hidden="true"
          />
          <div className="relative w-full max-w-md rounded-2xl border border-border dark:border-white/10 bg-surface dark:bg-[#0f172ae6] shadow-elevated p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                  <Icon name="Mail" size={18} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-text-primary">Recuperar contraseña</h3>
                  <p className="text-sm text-text-secondary">Ingresa el correo donde enviaremos el enlace de restablecimiento.</p>
                </div>
              </div>
              <button
                onClick={handleCloseResetModal}
                className="rounded-full p-1 text-text-secondary hover:bg-muted transition-smooth"
                aria-label="Cerrar"
              >
                <Icon name="X" size={16} />
              </button>
            </div>

            {resetStatus !== 'success' ? (
              <form onSubmit={handleResetPasswordRequest} className="space-y-4">
                <Input
                  label="Correo electrónico"
                  type="email"
                  name="resetEmail"
                  placeholder="tu-correo@empresa.com"
                  value={resetEmail}
                  onChange={(event) => setResetEmail(event?.target?.value)}
                  required
                  autoFocus
                  disabled={resetStatus === 'sending'}
                />

                {resetError && (
                  <div className="p-3 bg-error/10 dark:bg-error/20 border border-error/20 dark:border-error/40 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Icon name="AlertCircle" size={16} className="text-error" />
                      <span className="text-sm text-error">{resetError}</span>
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row-reverse gap-3 sm:items-center sm:justify-between">
                  <Button
                    type="submit"
                    variant="default"
                    fullWidth
                    loading={resetStatus === 'sending'}
                    iconName="Send"
                  >
                    Enviar enlace
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleCloseResetModal}
                    fullWidth
                    disabled={resetStatus === 'sending'}
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4 text-center">
                <div className="flex justify-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-success/10 dark:bg-success/20">
                    <Icon name="CheckCircle" size={26} className="text-success" />
                  </div>
                </div>
                <h4 className="text-lg font-semibold text-text-primary">¡Correo enviado!</h4>
                <p className="text-sm text-text-secondary">
                  Si la dirección <span className="font-medium text-text-primary">{resetEmail}</span> está registrada, recibirás un enlace para restablecer tu contraseña.
                </p>
                <Button variant="default" onClick={handleCloseResetModal} fullWidth>
                  Entendido
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginForm;
